import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import axios from "axios";
import config from "../config";
import {
  setEmail,
  setOtpSent,
  setOtpVerified,
  setRegistrationStep,
  setUser,
  setLoading,
  resetAuth,
} from "../redux/slice/Auth";
import loginbg from "../image/loginbg.jpg";

export default function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { email, registrationStep, loading } = useSelector(
    (state) => state.Auth
  );

  const [emailInput, setEmailInput] = useState("");
  const [otp, setOtp] = useState("");
  const [isNewUser, setIsNewUser] = useState(false);

  // Trainer registration form fields
  const [trainerData, setTrainerData] = useState({
    name: "",
    phone: "",
    countryCode: "+91", // Default to India
    city: "",
    experience: "",
  });

  // Country codes list
  const countryCodes = [
    { code: "+1", country: "USA/Canada" },
    { code: "+44", country: "UK" },
    { code: "+91", country: "India" },
    { code: "+61", country: "Australia" },
    { code: "+971", country: "UAE" },
    { code: "+65", country: "Singapore" },
    { code: "+60", country: "Malaysia" },
    { code: "+92", country: "Pakistan" },
    { code: "+880", country: "Bangladesh" },
    { code: "+94", country: "Sri Lanka" },
  ];

  useEffect(() => {
    // Check if user is already logged in
    const user = localStorage.getItem("user");
    if (user) {
      navigate("/dashboard");
    }
  }, [navigate]);

  const handleSendOtp = async (e) => {
    e.preventDefault();

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailInput || !emailRegex.test(emailInput)) {
      toast.error("Please enter a valid email address");
      return;
    }

    dispatch(setLoading(true));

    try {
      // Step 1: Check if user exists
      const checkEmailResponse = await axios.post(`${config.apiUrl}/trainers/check-email`, {
        email: emailInput,
      });

      console.log("Check Email Response:", checkEmailResponse.data);

      // Check the response structure: {success: true, exists: false, is_registered: false}
      if (checkEmailResponse.data.success) {
        if (checkEmailResponse.data.exists && checkEmailResponse.data.is_registered) {
          // Existing registered user - will use login flow after OTP
          setIsNewUser(false);

          // Step 2: Send OTP for existing user
          try {
            const otpResponse = await axios.post(`${config.apiUrl}/trainers/send-otp`, {
              email: emailInput,
            });

            if (otpResponse.data.success) {
              toast.success(otpResponse.data.message || "OTP sent to your email!");
              dispatch(setEmail(emailInput));
              dispatch(setOtpSent(true));
              dispatch(setRegistrationStep("otp"));
            }
          } catch (otpError) {
            console.error("OTP Send Error:", otpError);
            toast.error("Failed to send OTP. Please try again.");
            dispatch(setLoading(false));
            return;
          }
        } else {
          // New user - not registered, will use registration flow after OTP
          setIsNewUser(true);

          // Step 2: Send OTP for new user
          try {
            const otpResponse = await axios.post(`${config.apiUrl}/trainers/send-otp`, {
              email: emailInput,
            });

            if (otpResponse.data.success) {
              toast.success(otpResponse.data.message || "OTP sent successfully to your email");
              dispatch(setEmail(emailInput));
              dispatch(setOtpSent(true));
              dispatch(setRegistrationStep("otp"));
            }
          } catch (otpError) {
            console.error("OTP Send Error:", otpError);
            toast.error("Failed to send OTP. Please try again.");
            dispatch(setLoading(false));
            return;
          }
        }
      } else {
        toast.error("Failed to verify email. Please try again.");
      }
    } catch (error) {
      console.error("Email Check Error:", error);
      toast.error(error.response?.data?.message || "An error occurred. Please try again.");
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();

    if (!otp || otp.length < 4) {
      toast.error("Please enter a valid OTP");
      return;
    }

    dispatch(setLoading(true));

    try {
      if (isNewUser) {
        // New user - verify OTP and proceed to registration form
        // You can add OTP verification API call here if needed
        dispatch(setOtpVerified(true));
        dispatch(setRegistrationStep("details"));
        toast.success("OTP verified! Please complete your profile");
      } else {
        // Existing user - verify OTP and login
        const response = await axios.post(`${config.apiUrl}/trainers/login`, {
          email: emailInput,
          otp: otp,
        });

        if (response.data.success) {
          const userData = response.data.data || response.data.user;
          const token = response.data.token;

          dispatch(setUser(userData));
          localStorage.setItem("user", JSON.stringify(userData));
          localStorage.setItem("token", token);
          toast.success("Login successful!");
          navigate("/dashboard");
        } else {
          toast.error(response.data.message || "Login failed. Please try again.");
        }
      }
    } catch (error) {
      console.error("OTP Verification Error:", error);
      toast.error(error.response?.data?.message || "Invalid OTP. Please try again.");
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleTrainerRegistration = async (e) => {
    e.preventDefault();

    // Validation
    if (!trainerData.name || !trainerData.phone || !trainerData.city || !trainerData.experience) {
      toast.error("Please fill all required fields");
      return;
    }

    // Phone number validation (should be 10 digits for most countries)
    if (trainerData.phone.length < 10) {
      toast.error("Please enter a valid phone number");
      return;
    }

    dispatch(setLoading(true));

    try {
      const response = await axios.post(`${config.apiUrl}/trainers/register`, {
        email: emailInput,
        name: trainerData.name,
        country_code: trainerData.countryCode,
        phone: trainerData.phone,
        city: trainerData.city,
        experience: trainerData.experience,
        otp: otp, // Include OTP for verification
      });

      if (response.data.success) {
        const userData = response.data.data || response.data.user;
        const token = response.data.token;

        dispatch(setUser(userData));
        localStorage.setItem("user", JSON.stringify(userData));
        localStorage.setItem("token", token);
        toast.success(response.data.message || "Registration successful! Welcome to MeraGym");
        navigate("/dashboard");
      } else {
        toast.error(response.data.message || "Registration failed. Please try again.");
      }
    } catch (error) {
      console.error("Registration Error:", error);
      toast.error(error.response?.data?.message || "Registration failed. Please try again.");
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTrainerData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleReset = () => {
    dispatch(resetAuth());
    setEmailInput("");
    setOtp("");
    setIsNewUser(false);
    setTrainerData({
      name: "",
      phone: "",
      countryCode: "+91",
      city: "",
      experience: "",
    });
  };

  return (
    <>
      <section className="auth_section">
        <div className="container">
          <div className="login-form">
            <div className="row">
              <div className="col-lg-5 col-md-5">
                <div className="login-left-bg">
                  <img src={loginbg} alt="Login" />
                </div>
              </div>
              <div className="col-lg-7 col-md-7">
                <div className="login-form-box">
                  {/* Email Entry Step */}
                  {registrationStep === "email" && (
                    <>
                      <h4>Welcome To MeraGym</h4>
                      <p className="login-content">
                        {isNewUser
                          ? "Register as a Fitness Trainer"
                          : "Enter your email to continue"}
                      </p>
                      <form onSubmit={handleSendOtp}>
                        <div className="form-group">
                          <label htmlFor="email">Email Address *</label>
                          <input
                            type="email"
                            className="form-control"
                            id="email"
                            name="email"
                            placeholder="Enter your email address"
                            value={emailInput}
                            onChange={(e) => setEmailInput(e.target.value)}
                            required
                          />
                          <small className="text-muted">
                            We'll send a verification code to this email
                          </small>
                        </div>
                        <div className="form-footer">
                          <button
                            type="submit"
                            className="btn btn-danger w-100"
                            disabled={loading}
                          >
                            {loading ? "Sending..." : "Send OTP"}
                            <i className="bi bi-arrow-right ms-2"></i>
                          </button>
                          <p>
                            By continuing, you agree to our{" "}
                            <Link to="/terms-of-use">Terms &amp; conditions</Link> and{" "}
                            <Link to="/privacy-policy">Privacy Policy</Link>
                          </p>
                        </div>
                      </form>
                    </>
                  )}

                  {/* OTP Verification Step */}
                  {registrationStep === "otp" && (
                    <>
                      <h4>Verify OTP</h4>
                      <p className="login-content">
                        Enter the OTP sent to {email}
                        <button
                          type="button"
                          className="btn btn-link p-0 ms-2"
                          onClick={handleReset}
                        >
                          Change
                        </button>
                      </p>
                      <form onSubmit={handleVerifyOtp}>
                        <div className="form-group">
                          <label htmlFor="otp">Enter OTP *</label>
                          <input
                            type="number"
                            className="form-control"
                            id="otp"
                            name="otp"
                            placeholder="Enter OTP sent to your email"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value)}
                            onWheel={(e) => e.target.blur()}
                            maxLength={6}
                            required
                          />
                          <small className="text-muted">
                            Please check your email for the OTP
                          </small>
                        </div>
                        <div className="form-footer">
                          <button
                            type="submit"
                            className="btn btn-danger w-100"
                            disabled={loading}
                          >
                            {loading ? "Verifying..." : "Verify OTP"}
                            <i className="bi bi-arrow-right ms-2"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-link w-100 mt-2"
                            onClick={handleReset}
                          >
                            Back to Email
                          </button>
                        </div>
                      </form>
                    </>
                  )}

                  {/* Trainer Registration Form */}
                  {registrationStep === "details" && isNewUser && (
                    <>
                      <h4>Complete Your Profile</h4>
                      <p className="login-content">
                        Fill in your details to start your journey as a trainer
                      </p>
                      <form onSubmit={handleTrainerRegistration}>
                        <div className="form-group">
                          <label htmlFor="name">Full Name *</label>
                          <input
                            type="text"
                            className="form-control"
                            id="name"
                            name="name"
                            placeholder="Enter your full name"
                            value={trainerData.name}
                            onChange={handleInputChange}
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="phone">Phone Number *</label>
                          <div className="row g-2">
                            <div className="col-4">
                              <select
                                className="form-select"
                                name="countryCode"
                                value={trainerData.countryCode}
                                onChange={handleInputChange}
                                required
                              >
                                {countryCodes.map((item, index) => (
                                  <option key={index} value={item.code}>
                                    {item.code} ({item.country})
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="col-8">
                              <input
                                type="tel"
                                className="form-control"
                                id="phone"
                                name="phone"
                                placeholder="Enter phone number"
                                value={trainerData.phone}
                                onChange={handleInputChange}
                                onWheel={(e) => e.target.blur()}
                                required
                              />
                            </div>
                          </div>
                          <small className="text-muted">
                            Enter phone number without country code
                          </small>
                        </div>

                        <div className="form-group">
                          <label htmlFor="city">City *</label>
                          <input
                            type="text"
                            className="form-control"
                            id="city"
                            name="city"
                            placeholder="Enter your city"
                            value={trainerData.city}
                            onChange={handleInputChange}
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="experience">Years of Experience *</label>
                          <input
                            type="number"
                            className="form-control"
                            id="experience"
                            name="experience"
                            placeholder="Enter years of experience"
                            value={trainerData.experience}
                            onChange={handleInputChange}
                            min="0"
                            required
                          />
                        </div>

                        <div className="form-footer">
                          <button
                            type="submit"
                            className="btn btn-danger w-100"
                            disabled={loading}
                          >
                            {loading ? "Registering..." : "Complete Registration"}
                            <i className="bi bi-check-circle ms-2"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-link w-100 mt-2"
                            onClick={handleReset}
                          >
                            Start Over
                          </button>
                        </div>
                      </form>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
